import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { adminApi } from "../api/adminApi";
import DatePicker, { nextDay } from "./DatePicker";
import MonthBar from "./MonthBar";
import HelpTip from "./HelpTip";
import "./hotelInventory.css";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const emptyBulk = () => ({
  fromDate: "", toDate: "",
  roomsOpen: "", minStay: "",
  stopSell: false, closedToArrival: false, closedToDeparture: false,
  apply: { roomsOpen: false, minStay: false, stopSell: false, closedToArrival: false, closedToDeparture: false },
});

const AvailabilityCalendar = ({ roomTypeId, roomName, onClose }) => {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [nights, setNights] = useState([]);
  const [physicalRooms, setPhysicalRooms] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [single, setSingle] = useState(null);
  const [bulk, setBulk] = useState(emptyBulk());
  const [toast, setToast] = useState(null);
  const [singleRoomsErr, setSingleRoomsErr] = useState("");
  const [bulkRoomsErr, setBulkRoomsErr] = useState("");

  const flash = (type, text) => setToast({ type, text });
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 2200); return () => clearTimeout(t); }, [toast]);

  const first = useMemo(() => new Date(month.getFullYear(), month.getMonth(), 1), [month]);
  const last = useMemo(() => new Date(month.getFullYear(), month.getMonth() + 1, 0), [month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi("GET", `/admin/inventory/availability?roomTypeId=${roomTypeId}&fromDate=${ymd(first)}&toDate=${ymd(last)}`);
      const d = res?.data?.data || {};
      setNights(d.nights || []);
      setPhysicalRooms(d.roomType?.physicalRooms || 0);
    } finally {
      setLoading(false);
    }
  }, [roomTypeId, first, last]);
  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => new Map(nights.map((n) => [n.date, n])), [nights]);

  const exceedsCeiling = (v) => {
    if (v === "" || v === null || v === undefined) return false;
    const n = Number(v);
    if (!Number.isFinite(n)) return false;
    return n > (Number(physicalRooms) || 0);
  };
  const ceilingMsg = () => (physicalRooms > 0
    ? `Rooms open can't exceed ${physicalRooms} (physical ceiling).`
    : "Physical rooms is 0 — set it on the room first. No rooms can be opened.");

  const pickDay = (dateStr) => {
    const n = byDate.get(dateStr);
    setSingleRoomsErr("");
    setSelected(dateStr);
    setSingle({
      roomsOpen: n?.roomsOpen ?? "",
      minStay: n?.minStay ?? 1,
      stopSell: !!n?.stopSell,
      closedToArrival: !!n?.closedToArrival,
      closedToDeparture: !!n?.closedToDeparture,
    });
  };

  const saveSingle = async () => {
    if (exceedsCeiling(single.roomsOpen)) {
      setSingleRoomsErr(ceilingMsg());
      return;
    }
    setSingleRoomsErr("");
    try {
      await adminApi("POST", "/admin/inventory/availability", { roomTypeId, date: selected, values: single });
      flash("ok", "Saved");
      setSelected(null);
      setSingle(null);
      load();
    } catch (e) {
      flash("err", e?.response?.data?.message || "Save failed");
    }
  };

  const applyBulk = async () => {
    if (!bulk.fromDate || !bulk.toDate) { flash("err", "Pick a date range"); return; }
    if (bulk.apply.roomsOpen && exceedsCeiling(bulk.roomsOpen)) {
      setBulkRoomsErr(ceilingMsg());
      return;
    }
    setBulkRoomsErr("");
    const values = {};
    ["roomsOpen", "minStay"].forEach((k) => { if (bulk.apply[k] && bulk[k] !== "") values[k] = bulk[k]; });
    ["stopSell", "closedToArrival", "closedToDeparture"].forEach((k) => { if (bulk.apply[k]) values[k] = bulk[k]; });
    if (!Object.keys(values).length) { flash("err", "Choose a value or restriction to apply"); return; }
    try {
      await adminApi("POST", "/admin/inventory/availability", { roomTypeId, fromDate: bulk.fromDate, toDate: bulk.toDate, values });
      flash("ok", "Range updated");
      setBulk(emptyBulk());
      load();
    } catch (e) {
      flash("err", e?.response?.data?.message || "Update failed");
    }
  };

  const cells = useMemo(() => {
    const lead = first.getDay();
    const total = last.getDate();
    const arr = [];
    for (let i = 0; i < lead; i += 1) arr.push(null);
    for (let day = 1; day <= total; day += 1) arr.push(ymd(new Date(month.getFullYear(), month.getMonth(), day)));
    return arr;
  }, [first, last, month]);

  const setBulkField = (k, v) => setBulk((b) => ({ ...b, [k]: v }));
  const toggleApply = (k) => setBulk((b) => ({ ...b, apply: { ...b.apply, [k]: !b.apply[k] } }));
  const triMode = (k) => (!bulk.apply[k] ? "leave" : (bulk[k] ? "on" : "off"));
  const setTri = (k, mode) => setBulk((b) => ({ ...b, apply: { ...b.apply, [k]: mode !== "leave" }, [k]: mode === "on" }));

  return (
    <div className="hi-modal-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="hi-modal">
        {toast ? <div className={`toast ${toast.type}`}>{toast.text}</div> : null}
        <div className="hi-modal-head">
          <h3>Room availability — {roomName} <HelpTip title="Opening rooms to sell">
            <p>This is how many of this room you have open to sell each night. It is shared by all the room's rate plans.</p>
            <ul>
              <li><strong>Colours:</strong> green = in stock, amber = only a few left, red = sold out / closed.</li>
              <li><strong>Click a day</strong> to set rooms open for that night, or stop-sell it.</li>
              <li><strong>Bulk edit</strong> sets a whole date range — e.g. open 10 rooms for the summer, or close a blackout period.</li>
              <li>A night sells only when it has <em>both</em> a price (in Rate plans → Prices) and rooms open here.</li>
            </ul>
          </HelpTip></h3>
          <button className="hi-x" onClick={onClose}>×</button>
        </div>
        <div className="hi-modal-body">
          <div className="hi-sub" style={{ marginBottom: 10 }}>Rooms &amp; sale rules — shared by every rate plan. Physical rooms (ceiling): <strong>{physicalRooms}</strong>. Click a day to edit it, or use bulk edit below.</div>

          <MonthBar month={month} onChange={setMonth} />

          {loading ? (
            <div className="hi-empty">Loading…</div>
          ) : (
            <div className="hi-cal7">
              {DOW.map((d) => <div className="hi-dow" key={d}>{d}</div>)}
              {cells.map((dateStr, i) => {
                if (!dateStr) return <div className="hi-daycell blank" key={`b${i}`} />;
                const n = byDate.get(dateStr);
                const day = Number(dateStr.slice(-2));
                const isPast = dateStr < ymd(new Date());
                const avail = Number(n?.available) || 0;
                const stock = n && !n.stopSell ? (avail <= 0 ? "out" : avail <= 5 ? "low" : "ok") : "";
                const cls = `hi-daycell${selected === dateStr ? " is-sel" : ""}${n?.stopSell ? " stop" : ""}${stock ? ` stock-${stock}` : ""}${isPast ? " past" : ""}`;
                return (
                  <div className={cls} key={dateStr} onClick={() => { if (!isPast) pickDay(dateStr); }}>
                    <div className="dn">{day}</div>
                    {n ? (
                      n.stopSell ? <div className="no">Stopped</div>
                        : avail <= 0 ? <div className="no">Out of stock</div>
                        : <div className={`av${avail <= 5 ? " low" : ""}`}>{avail} left</div>
                    ) : <div className="pr">—</div>}
                  </div>
                );
              })}
            </div>
          )}

          {selected && single && (
            <div className="hi-section" style={{ marginTop: 16 }}>
              <h3>Edit {selected}</h3>
              <div className="hi-grid two">
                <div className="hi-fld"><label>Rooms open</label><input className={`inp${singleRoomsErr ? " err" : ""}`} type="number" min="0" value={single.roomsOpen} onChange={(e) => { setSingle({ ...single, roomsOpen: e.target.value }); setSingleRoomsErr(""); }} />{singleRoomsErr ? <span className="hi-err">{singleRoomsErr}</span> : (physicalRooms > 0 ? <span className="hi-sub" style={{ marginTop: 5 }}>You can open up to {physicalRooms} room{physicalRooms > 1 ? "s" : ""}</span> : <span className="hi-sub" style={{ marginTop: 5 }}>Set physical rooms on the room to open availability.</span>)}</div>
                <div className="hi-fld"><label>Minimum stay</label><input className="inp" type="number" value={single.minStay} onChange={(e) => setSingle({ ...single, minStay: e.target.value })} /></div>
              </div>
              <div className="hi-bulk-sub">Restrictions</div>
              <div className="hi-chkrow">
                <label className="hi-chkpill"><input type="checkbox" checked={single.stopSell} onChange={(e) => setSingle({ ...single, stopSell: e.target.checked })} /> Stop sell</label>
                <label className="hi-chkpill"><input type="checkbox" checked={single.closedToArrival} onChange={(e) => setSingle({ ...single, closedToArrival: e.target.checked })} /> Closed to arrival</label>
                <label className="hi-chkpill"><input type="checkbox" checked={single.closedToDeparture} onChange={(e) => setSingle({ ...single, closedToDeparture: e.target.checked })} /> Closed to departure</label>
              </div>
              <div className="hi-inline" style={{ marginTop: 14, justifyContent: "flex-end" }}>
                <button className="btn ghost" onClick={() => { setSelected(null); setSingle(null); }}>Cancel</button>
                <button className="btn primary" onClick={saveSingle}>Save day</button>
              </div>
            </div>
          )}

          <div className="hi-section" style={{ marginTop: 16 }}>
            <h3>Bulk edit (date range)</h3>
            <div className="hi-grid two">
              <div className="hi-fld"><label>From</label><DatePicker value={bulk.fromDate} onChange={(v) => setBulk((b) => ({ ...b, fromDate: v, toDate: (!b.toDate || b.toDate <= v) ? nextDay(v) : b.toDate }))} /></div>
              <div className="hi-fld"><label>To</label><DatePicker value={bulk.toDate} min={bulk.fromDate} onChange={(v) => setBulkField("toDate", v)} /></div>
            </div>

            <div className="hi-bulk-sub">Values <span>— tick to include, then enter the value</span></div>
            <div className="hi-grid two">
              {[["roomsOpen", "Rooms open"], ["minStay", "Minimum stay"]].map(([k, label]) => (
                <div className="hi-fld" key={k}>
                  <label className="hi-chkline"><input type="checkbox" checked={bulk.apply[k]} onChange={() => toggleApply(k)} /> {label}</label>
                  <input className={`inp${k === "roomsOpen" && bulkRoomsErr ? " err" : ""}`} type="number" min="0" disabled={!bulk.apply[k]} value={bulk[k]} onChange={(e) => { setBulkField(k, e.target.value); if (k === "roomsOpen") setBulkRoomsErr(""); }} placeholder={bulk.apply[k] ? "" : "Not changing"} />
                  {k === "roomsOpen" ? (bulkRoomsErr ? <span className="hi-err">{bulkRoomsErr}</span> : (physicalRooms > 0 ? <span className="hi-sub" style={{ marginTop: 5 }}>Up to {physicalRooms} room{physicalRooms > 1 ? "s" : ""} (physical ceiling)</span> : <span className="hi-sub" style={{ marginTop: 5 }}>Set physical rooms to open availability.</span>)) : null}
                </div>
              ))}
            </div>

            <div className="hi-bulk-sub">Restrictions <span>— set across the whole range</span> <HelpTip title="What these restrictions mean">
              <ul>
                <li><strong>Stop sell</strong> — the night is fully closed; no booking can use it, even if rooms are open.</li>
                <li><strong>Closed to arrival</strong> — guests can't <em>check in</em> on that night (a stay that started earlier may continue through it).</li>
                <li><strong>Closed to departure</strong> — guests can't <em>check out</em> that morning (a booking can't end there).</li>
              </ul>
              <p><strong>Leave / On / Off</strong> — because this edits a whole date range: <strong>Leave</strong> keeps each night as-is, <strong>On</strong> turns the rule on for every night, <strong>Off</strong> clears it for every night.</p>
              <p><strong>Apply to range</strong> writes these to every night in the From→To range; anything left on Leave is untouched.</p>
            </HelpTip></div>
            <div className="hi-bulk-rules">
              {[["stopSell", "Stop sell"], ["closedToArrival", "Closed to arrival"], ["closedToDeparture", "Closed to departure"]].map(([k, label]) => (
                <div className="hi-bulk-rule" key={k}>
                  <span className="hi-bulk-rule-lbl">{label}</span>
                  <div className="hi-seg">
                    <button type="button" className={`hi-seg-btn${triMode(k) === "leave" ? " is-active leave" : ""}`} onClick={() => setTri(k, "leave")}>Leave</button>
                    <button type="button" className={`hi-seg-btn${triMode(k) === "on" ? " is-active on" : ""}`} onClick={() => setTri(k, "on")}>On</button>
                    <button type="button" className={`hi-seg-btn${triMode(k) === "off" ? " is-active off" : ""}`} onClick={() => setTri(k, "off")}>Off</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hi-inline" style={{ marginTop: 16, justifyContent: "flex-end" }}>
              <button className="btn primary" onClick={applyBulk}>Apply to range</button>
            </div>
          </div>
        </div>
        <div className="hi-modal-foot">
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
